import { getStoreState, store } from '@/store';
import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

// * Shadcn UI
const twMerge = extendTailwindMerge({ prefix: '$tw-' });

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(...inputs));
}

const isAuthenticated = (): boolean => {
  const state = getStoreState();

  return !!state.userSession.user;
};

// * Auth utils
const checkAuth = async () => {
  await new Promise<void>((resolve) => {
    const state = store.getState();
    // * If not logging in, resolve immediately.
    if (!state.userSession.isLoggingIn) {
      resolve();
    }

    // * If logging in, subscribe to the store to wait for it to finish.
    const unsubscribe = store.subscribe(() => {
      const updatedState = store.getState();
      if (!updatedState.userSession.isLoggingIn) {
        unsubscribe();
        resolve();
      }
    });
  });
};

const userIsAdmin = (): boolean => {
  const state = getStoreState();
  return !!state.userSession.user?.is_staff;
};

export const authUtils = {
  isAuthenticated,
  checkAuth,
  userIsAdmin,
};

export type AuthUtils = typeof authUtils;

// * HEX to HSL colors for theme to TailwindCSS
export function hexToHSL(hex: string): string {
  hex = hex.replace(/^#/, '');

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  let l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  return `${h} ${s}% ${l}%`;
}
